import { Navigate, Routes as ReactRoutes, Route } from 'react-router-dom';

import * as Sentry from '@sentry/react';

import { DYNAMIC_IMPORTS } from '@/chunks/chunkMapping';
import SessionProviderWrapper from '@/components/shared/SessionProviderWrapper';
import AnalyticsAssistantLayout from '@/pages/AnalyticsPage/components/AnalyticsAssistant/AnalyticsAssistantLayout';
import { MOBILE_EDITOR_ROUTES } from '@/pages/MobileEditorPage/constants/routes';
import WithPermissionAccess from '@/providers/UserPermissionsProvider/components/WithPermissionAccess';
import { PERMISSIONS } from '@/utils/permissionTypes';
import { ROLE_NAME_MAP } from '@/utils/roleUtils';

import BaseLayout from './components/shared/BaseLayout';
import StudioAuthorizationCallback from './components/shared/ImageUpload/StudioImport/StudioAuthorizationCallback/StudioAuthorizationCallback';
import AccessDeclined from './pages/AccessRequired/AccessDeclined';
import AccessRequest from './pages/AccessRequired/AccessRequest';
import AccessRequired from './pages/AccessRequired/AccessRequired';
import CallbackPage from './pages/CallbackPage';
import WithDevelopersAccess from './pages/Guards/WithDevelopAccess';
import WithFeatureAccess from './pages/Guards/WithFeatureAccess';
import WithRouteContext from './pages/Guards/WithRouteContext';
import WithStaffAccess from './pages/Guards/WithStaffAccess';
import NotFound from './pages/NotFound';
import ProgramProvider from './providers/ProgramProvider/ProgramProvider';
import withDynamicImport from './utils/hoc/withDynamicImport';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(ReactRoutes);

// NOTE: When adding new pages, ensure the order
// matches the navList from PrimaryNavDrawer.tsx

// Dynamic imported pages that show on nav bar
// Campaigns page
const CampaignCalendarPage = withDynamicImport(
  DYNAMIC_IMPORTS.CAMPAIGN_INDEX_PAGE.name,
);
const CampaignEditPage = withDynamicImport(
  DYNAMIC_IMPORTS.CAMPAIGN_EDIT_PAGE.name,
);
const MessagesBuilderEditorPage = withDynamicImport(
  DYNAMIC_IMPORTS.MESSAGES_BUILDER_EDITOR_PAGE.name,
);
const RecurringCampaignEditPage = withDynamicImport(
  DYNAMIC_IMPORTS.RECURRING_CAMPAIGN_EDIT_PAGE.name,
);
const BaseTemplateBuilderEditorPage = withDynamicImport(
  DYNAMIC_IMPORTS.BASE_TEMPLATE_BUILDER_EDITOR_PAGE.name,
);
const RecurringMessagesBuilderEditorPage = withDynamicImport(
  DYNAMIC_IMPORTS.RECURRING_MESSAGES_BUILDER_EDITOR_PAGE.name,
);
const TriggeredCampaignEditPage = withDynamicImport(
  DYNAMIC_IMPORTS.TRIGGERED_CAMPAIGN_EDIT_PAGE.name,
);
const TriggeredMessagesBuilderEditorPage = withDynamicImport(
  DYNAMIC_IMPORTS.TRIGGERED_MESSAGES_BUILDER_EDITOR_PAGE.name,
);
const ProgramsList = withDynamicImport(DYNAMIC_IMPORTS.PROGRAMS_LIST.name);
const ProgramCreatePage = withDynamicImport(
  DYNAMIC_IMPORTS.PROGRAM_CREATE_PAGE.name,
);
const ProgramDetailsPage = withDynamicImport(
  DYNAMIC_IMPORTS.PROGRAM_DETAILS_PAGE.name,
);

// Creatives page
const CreativeIndexPage = withDynamicImport(
  DYNAMIC_IMPORTS.CREATIVE_INDEX_PAGE.name,
);
const CreativeContentEditorPage = withDynamicImport(
  DYNAMIC_IMPORTS.CREATIVE_CONTENT_EDITOR_PAGE.name,
);
const CreativeContentEditorV2Page = withDynamicImport(
  DYNAMIC_IMPORTS.CREATIVE_CONTENT_EDITOR_V2_PAGE.name,
);
const CreativeEditPage = withDynamicImport(
  DYNAMIC_IMPORTS.CREATIVE_EDIT_PAGE.name,
);
const ContentEditorAbstractionTestPage = withDynamicImport(
  DYNAMIC_IMPORTS.CONTENT_EDITOR_ABSTRACTION_TEST_PAGE.name,
);
const RichTextEditorTestPage = withDynamicImport(
  DYNAMIC_IMPORTS.RICH_TEXT_EDITOR_TEST_PAGE.name,
);
const ContentComponentsEditorPage = withDynamicImport(
  DYNAMIC_IMPORTS.CONTENT_COMPONENTS_EDITOR_PAGE.name,
);
const BlocksEditorPage = withDynamicImport(
  DYNAMIC_IMPORTS.BLOCKS_EDITOR_PAGE.name,
);
const MobileEditorPage = withDynamicImport(
  DYNAMIC_IMPORTS.MOBILE_EDITOR_PAGE.name,
);

// Audience page
const AudiencesPage = withDynamicImport(DYNAMIC_IMPORTS.AUDIENCES_PAGE.name);
const AudienceBuilderUnified = withDynamicImport(
  DYNAMIC_IMPORTS.AUDIENCE_BUILDER_UNIFIED.name,
);
const AudienceBuilderPage = withDynamicImport(
  DYNAMIC_IMPORTS.AUDIENCE_BUILDER_PAGE.name,
);
const AudienceDetailPage = withDynamicImport(
  DYNAMIC_IMPORTS.AUDIENCE_DETAIL_PAGE.name,
);

// Analytics page
const AnalyticsDashboard = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_DASHBOARD.name,
);
const AnalyticsCampaignsAPIMigration = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_CAMPAIGNS_APIMigration.name,
);
const AnalyticsCampaignDetailsAPIMigration = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_CAMPAIGN_DETAILS_API_MIGRATION.name,
);
const AnalyticsRecurringCampaignDetailsAPIMigration = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_RECURRING_CAMPAIGN_DETAILS_API_MIGRATION.name,
);
const AnalyticsPrograms = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_PROGRAMS.name,
);
const AnalyticsProgramDetails = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_PROGRAM_DETAILS.name,
);
const AnalyticsContent = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_CONTENT.name,
);
const AnalyticsAudiences = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_AUDIENCES.name,
);
const AnalyticsCreativesAPIMigration = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_CREATIVES_APIMIGRATION.name,
);
const AnalyticsCreativesV2 = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_CREATIVES_V2.name,
);

const AnalyticsCreativeDetailsAPIMigration = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_CREATIVE_DETAILS_APIMIGRATION.name,
);
const AnalyticsCreativeDetailsV2 = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_CREATIVE_DETAILS_V2.name,
);
const AnalyticsSubjectLinesAPIMigration = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_SUBJECT_LINES_APIMIGRATION.name,
);
const AnalyticsSegmentsAPIMigration = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_SEGMENTS_APIMIGRATION.name,
);
const AnalyticsSegmentsCreate = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_SEGMENTS_CREATE.name,
);
const AnalyticsExperimentsAPIMigration = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_EXPERIMENTS_APIMIGRATION.name,
);
const AnalyticsExperimentDetailsAPIMigration = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_EXPERIMENT_DETAILS_APIMIGRATION.name,
);
const AnalyticsSegmentDetailsAPIMigration = withDynamicImport(
  DYNAMIC_IMPORTS.ANALYTICS_SEGMENT_DETAILS_APIMIGRATION.name,
);

// Data connections page
const DataConnectionsPage = withDynamicImport(
  DYNAMIC_IMPORTS.DATA_CONNECTIONS_PAGE.name,
);
const DataConnectionsCreatePage = withDynamicImport(
  DYNAMIC_IMPORTS.DATA_CONNECTIONS_CREATE_PAGE.name,
);
const DataConnectionsDetailPage = withDynamicImport(
  DYNAMIC_IMPORTS.DATA_CONNECTIONS_DETAIL_PAGE.name,
);

// Tables page
const TablesPage = withDynamicImport(DYNAMIC_IMPORTS.TABLES_PAGE.name);
const TableCreateAndEditPage = withDynamicImport(
  DYNAMIC_IMPORTS.TABLES_CREATE_AND_EDIT_PAGE.name,
);

// Jobs page
const JobsPage = withDynamicImport(DYNAMIC_IMPORTS.JOBS_PAGE.name);
const JobDetailPage = withDynamicImport(DYNAMIC_IMPORTS.JOB_DETAIL_PAGE.name);
const JobExportDetailPage = withDynamicImport(
  DYNAMIC_IMPORTS.JOB_EXPORT_DETAIL_PAGE.name,
);
const JobsCreatePage = withDynamicImport(DYNAMIC_IMPORTS.JOB_CREATE_PAGE.name);

// Datasets page
const DatasetsPage = withDynamicImport(DYNAMIC_IMPORTS.DATASETS_PAGE.name);
const DatasetCreatePage = withDynamicImport(
  DYNAMIC_IMPORTS.DATASET_CREATE_PAGE.name,
);
const DatasetDetailPage = withDynamicImport(
  DYNAMIC_IMPORTS.DATASET_DETAIL_PAGE.name,
);
const DatasetSourcePage = withDynamicImport(
  DYNAMIC_IMPORTS.DATASET_SOURCE_PAGE.name,
);
const DatasetJobDetailPage = withDynamicImport(
  DYNAMIC_IMPORTS.DATASET_JOB_DETAIL_PAGE.name,
);

// Brand Kit page
const BrandKitPage = withDynamicImport(DYNAMIC_IMPORTS.BRAND_KIT_PAGE.name);
const BrandKitDetailsPage = withDynamicImport(
  DYNAMIC_IMPORTS.BRAND_KIT_DETAILS_PAGE.name,
);

// Settings page
const SettingsPage = withDynamicImport(DYNAMIC_IMPORTS.SETTINGS_PAGE.name);
const Organizations = withDynamicImport(DYNAMIC_IMPORTS.ORGANIZATIONS.name);
const UsersSettings = withDynamicImport(DYNAMIC_IMPORTS.USERS_SETTINGS.name);
const UserEdit = withDynamicImport(DYNAMIC_IMPORTS.USER_EDIT.name);
const AccountsEdit = withDynamicImport(DYNAMIC_IMPORTS.ACCOUNTS_EDIT.name);
const AccountsRolesEdit = withDynamicImport(
  DYNAMIC_IMPORTS.ACCOUNTS_ROLES_EDIT.name,
);
const CreateRolePage = withDynamicImport(DYNAMIC_IMPORTS.CREATE_ROLE_PAGE.name);
const ApplicationEditPage = withDynamicImport(
  DYNAMIC_IMPORTS.APPLICATION_EDIT_PAGE.name,
);
const NotificationSettings = withDynamicImport(
  DYNAMIC_IMPORTS.NOTIFICATION_SETTINGS.name,
);

// Dynamic imported pages that don't show on nav bar

// Graphiql page
const GraphiQLComponent = withDynamicImport(
  DYNAMIC_IMPORTS.GRAPHIQL_COMPONENT.name,
);

// Journey page
const JourneyPage = withDynamicImport(DYNAMIC_IMPORTS.JOURNEY_PAGE.name);

// Ink AI page
const InkAiPage = withDynamicImport(DYNAMIC_IMPORTS.INK_AI_PAGE.name);

// Add any new pages that aren't navigable from primary nav here

const Routes = () => {
  return (
    <SentryRoutes>
      <Route path="access/declined/:accountId" element={<AccessDeclined />} />
      <Route element={<SessionProviderWrapper />}>
        <Route path="access/invite-request" element={<AccessRequest />} />
        <Route path="access/restricted" element={<AccessRequired />} />
        <Route element={<BaseLayout />}>
          <Route
            path="/"
            element={
              <WithRouteContext
                render={({ clientId }) => (
                  <Navigate to={`${clientId}/campaigns`} />
                )}
              />
            }
          />
          <Route path="/:clientId?">
            {/* wildcard passes sub-paths into the island's own <Routes> */}
            <Route
              path="ink-ai/*"
              element={
                // TODO: feature_flag_cleanup: enableAiFeatures
                <WithFeatureAccess featureFlag="enableAiFeatures">
                  {/* TODO: feature_flag_cleanup: hasCopilot */}
                  <WithFeatureAccess featureFlag="hasCopilot">
                    <InkAiPage />
                  </WithFeatureAccess>
                </WithFeatureAccess>
              }
            />
            <Route
              path="campaigns"
              element={
                <WithPermissionAccess permission={PERMISSIONS.CAMPAIGNS_READ}>
                  <CampaignCalendarPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="programs"
              element={
                <WithPermissionAccess permission={PERMISSIONS.CAMPAIGNS_READ}>
                  <ProgramsList />
                </WithPermissionAccess>
              }
            />
            <Route
              path="campaignslist"
              element={
                <WithPermissionAccess permission={PERMISSIONS.CAMPAIGNS_READ}>
                  <CampaignCalendarPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="program/create/:programId"
              element={
                <WithPermissionAccess permission={PERMISSIONS.CAMPAIGNS_CREATE}>
                  <ProgramProvider>
                    <ProgramCreatePage />
                  </ProgramProvider>
                </WithPermissionAccess>
              }
            />
            <Route
              path="campaigns/:campaignId/template/messages-builder"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CAMPAIGNS_UPDATE}
                  role={ROLE_NAME_MAP.editor}
                >
                  <MessagesBuilderEditorPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="base-templates/:baseTemplateId/messages-builder"
              element={
                <WithStaffAccess>
                  <BaseTemplateBuilderEditorPage />
                </WithStaffAccess>
              }
            />
            <Route
              path="recurring-campaigns/:campaignId/message/messages-builder"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CAMPAIGNS_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <WithFeatureAccess featureFlag="hasRecurringCampaigns">
                    <RecurringMessagesBuilderEditorPage />
                  </WithFeatureAccess>
                </WithPermissionAccess>
              }
            />
            <Route
              path="campaigns/:campaignId/:campaignEditTab"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CAMPAIGNS_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <CampaignEditPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="recurring-campaigns/:campaignId/:campaignEditTab"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CAMPAIGNS_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <WithFeatureAccess featureFlag="hasRecurringCampaigns">
                    <RecurringCampaignEditPage />
                  </WithFeatureAccess>
                </WithPermissionAccess>
              }
            />
            <Route
              path="triggered-campaigns/:campaignId/message/messages-builder"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CAMPAIGNS_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <WithFeatureAccess featureFlag="hasTriggeredCampaigns">
                    <TriggeredMessagesBuilderEditorPage />
                  </WithFeatureAccess>
                </WithPermissionAccess>
              }
            />
            <Route
              path="triggered-campaigns/:campaignId/:campaignEditTab"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CAMPAIGNS_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <WithFeatureAccess featureFlag="hasTriggeredCampaigns">
                    <TriggeredCampaignEditPage />
                  </WithFeatureAccess>
                </WithPermissionAccess>
              }
            />

            <Route
              path="creatives"
              element={
                <WithPermissionAccess permission={PERMISSIONS.CREATIVES_READ}>
                  <CreativeIndexPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="creatives/:creativeId/:creativeEditTab"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CREATIVES_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <CreativeEditPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="programs/:programId/:programEditTab"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CAMPAIGNS_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <ProgramProvider>
                    <ProgramDetailsPage />
                  </ProgramProvider>
                </WithPermissionAccess>
              }
            />
            <Route
              path="creatives/:creativeId/disclaimer/layout/:layoutId"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CREATIVES_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <CreativeContentEditorPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="creatives/:creativeId/content/layout-editor/:layoutId"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CREATIVES_UPDATE}
                  role={ROLE_NAME_MAP.editor}
                >
                  <CreativeContentEditorPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="creatives/:creativeId/content/layout-editor-v2/:layoutId"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CREATIVES_UPDATE}
                  role={ROLE_NAME_MAP.editor}
                >
                  {/* TODO: feature_flag_cleanup: hasCreativeEditorV2 */}
                  <WithFeatureAccess featureFlag="hasCreativeEditorV2">
                    <CreativeContentEditorV2Page />
                  </WithFeatureAccess>
                </WithPermissionAccess>
              }
            />
            {/* Content Editor Testing routes */}
            {/* Full Content Editor Route for Testing */}
            <Route
              path="creatives/:creativeId?/editor"
              element={<CreativeContentEditorPage />}
            />
            {/* Content Editor Abstraction */}
            <Route
              path="creatives/test-editor"
              element={<ContentEditorAbstractionTestPage />}
            />
            {/* Rich Text Editor Test */}
            <Route
              path="creatives/test-rich-editor"
              element={<RichTextEditorTestPage />}
            />
            {/* End Content Editor Testing routes */}

            {/* Channel-specific mobile editor routes */}
            <Route
              path={`creatives/:creativeId/${MOBILE_EDITOR_ROUTES.PUSH}`}
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CREATIVES_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <MobileEditorPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path={`creatives/:creativeId/${MOBILE_EDITOR_ROUTES.SMS}`}
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CREATIVES_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <MobileEditorPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path={`creatives/:creativeId/${MOBILE_EDITOR_ROUTES.MMS}`}
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CREATIVES_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <MobileEditorPage />
                </WithPermissionAccess>
              }
            />
            {/* Edit existing mobile message (any channel) */}
            <Route
              path="creatives/:creativeId/content/:contentId"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CREATIVES_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <MobileEditorPage />
                </WithPermissionAccess>
              }
            />
            {/* Backward compatibility - redirect old route to push-editor */}
            <Route
              path="creatives/:creativeId/mobile-editor"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.CREATIVES_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <Navigate to={`../${MOBILE_EDITOR_ROUTES.PUSH}`} replace />
                </WithPermissionAccess>
              }
            />

            <Route
              path="contacts/audiences-beta-v2/create"
              element={
                <WithPermissionAccess permission={PERMISSIONS.AUDIENCE_CREATE}>
                  <WithFeatureAccess
                    featureFlag={['hasUnifiedAudienceBuilder']}
                  >
                    <AudienceBuilderUnified createOrEditMode="create" />
                  </WithFeatureAccess>
                </WithPermissionAccess>
              }
            />
            <Route
              path="contacts/audiences-beta-v2/:audienceId"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.AUDIENCE_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <WithFeatureAccess
                    featureFlag={['hasUnifiedAudienceBuilder']}
                  >
                    <AudienceBuilderUnified createOrEditMode="edit" />
                  </WithFeatureAccess>
                </WithPermissionAccess>
              }
            />
            <Route
              path="contacts/audiences-beta"
              element={
                <WithPermissionAccess permission={PERMISSIONS.AUDIENCE_READ}>
                  <AudiencesPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="contacts/audiences-beta/create"
              element={
                <WithPermissionAccess permission={PERMISSIONS.AUDIENCE_CREATE}>
                  <AudienceBuilderPage createOrEditMode="create" />
                </WithPermissionAccess>
              }
            />
            <Route
              path="contacts/audiences-beta/:audienceId"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.AUDIENCE_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <AudienceBuilderPage createOrEditMode="edit" />
                </WithPermissionAccess>
              }
            />
            <Route
              path="contacts/audiences-beta/:audienceId/:audienceDetailTab"
              element={
                <WithPermissionAccess permission={PERMISSIONS.AUDIENCE_READ}>
                  <WithFeatureAccess featureFlag={['hasAudienceDetailPage']}>
                    <AudienceDetailPage />
                  </WithFeatureAccess>
                </WithPermissionAccess>
              }
            />

            <Route element={<AnalyticsAssistantLayout />}>
              <Route
                path="analytics/dashboard"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <WithFeatureAccess featureFlag="hasReportingDashboard">
                      <AnalyticsDashboard />
                    </WithFeatureAccess>
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/campaigns-v2/:savedViewId?"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <AnalyticsCampaignsAPIMigration />
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/campaigns-v2/:campaignId/:campaignDetailsTab"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <AnalyticsCampaignDetailsAPIMigration />
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/recurring-campaigns-v2/:campaignId/:campaignDetailsTab"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    {/* TODO: feature_flag_cleanup: hasReportingRecurringCampaigns */}
                    <WithFeatureAccess featureFlag="hasReportingRecurringCampaigns">
                      <AnalyticsRecurringCampaignDetailsAPIMigration />
                    </WithFeatureAccess>
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/content"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    {/* TODO: feature_flag_cleanup: hasUnifiedMessagingNav */}
                    <WithFeatureAccess
                      featureFlag="hasUnifiedMessagingNav"
                      fallbackComponent={
                        <WithRouteContext
                          render={({ clientId }) => (
                            <Navigate
                              to={`/${clientId}/analytics/creatives-v2`}
                              replace
                            />
                          )}
                        />
                      }
                    >
                      <AnalyticsContent />
                    </WithFeatureAccess>
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/audiences"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    {/* TODO: feature_flag_cleanup: hasUnifiedMessagingNav */}
                    <WithFeatureAccess
                      featureFlag="hasUnifiedMessagingNav"
                      fallbackComponent={
                        <WithRouteContext
                          render={({ clientId }) => (
                            <Navigate
                              to={`/${clientId}/analytics/experiments-v2`}
                              replace
                            />
                          )}
                        />
                      }
                    >
                      <AnalyticsAudiences />
                    </WithFeatureAccess>
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/creatives-v2/:savedViewId?"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <AnalyticsCreativesAPIMigration />
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/creatives-v2/:creativeId/:creativeDetailsTab"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <AnalyticsCreativeDetailsAPIMigration />
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/subject-lines-v2"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <AnalyticsSubjectLinesAPIMigration />
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/creatives-v2-new/:savedViewId?"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <AnalyticsCreativesV2 />
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/experiments-v2"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <AnalyticsExperimentsAPIMigration />
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/experiments-v2/:experimentId/:experimentDetailsTab"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <AnalyticsExperimentDetailsAPIMigration />
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/segments-v2"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <AnalyticsSegmentsAPIMigration />
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/segments-v2/:segmentId/:segmentDetailsTab"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <AnalyticsSegmentDetailsAPIMigration />
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/programs/:savedViewId?"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <AnalyticsPrograms />
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/creatives-v2-new/:creativeId/:creativeDetailsTab"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <AnalyticsCreativeDetailsV2 />
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/segments-v2/:segmentGroupId"
                element={
                  <WithPermissionAccess
                    permission={PERMISSIONS.ANALYTICS_CREATE}
                  >
                    <AnalyticsSegmentsCreate />
                  </WithPermissionAccess>
                }
              />
              <Route
                path="analytics/programs/:programId/:programDetailsTab"
                element={
                  <WithPermissionAccess permission={PERMISSIONS.ANALYTICS_READ}>
                    <AnalyticsProgramDetails />
                  </WithPermissionAccess>
                }
              />
            </Route>
            <Route
              path="data/connections"
              element={
                <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                  <DataConnectionsPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="data/connections/create-v2"
              element={
                <WithPermissionAccess permission={PERMISSIONS.DATA_CREATE}>
                  <DataConnectionsCreatePage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="data/connections/:dataConnectionId"
              element={
                <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                  <Navigate to="overview" />
                </WithPermissionAccess>
              }
            />
            <Route
              path="data/connections/:dataConnectionId/:dataConnectionDetailTab"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.DATA_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <DataConnectionsDetailPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="data/tables"
              element={
                <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                  <TablesPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="data/tables/:tableId"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.DATA_READ}
                  role={ROLE_NAME_MAP.editor}
                >
                  <TableCreateAndEditPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="data/jobs"
              element={
                <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                  <JobsPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="data/jobs/create"
              element={
                <WithPermissionAccess permission={PERMISSIONS.DATA_CREATE}>
                  <JobsCreatePage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="data/jobs/:jobId"
              element={
                <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                  <Navigate to="overview" />
                </WithPermissionAccess>
              }
            />
            <Route
              path="data/jobs/:jobId/overview"
              element={
                <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                  <JobDetailPage currentTab="overview" />
                </WithPermissionAccess>
              }
            />
            <Route
              path="data/jobs/:jobId/run-history"
              element={
                <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                  <JobDetailPage currentTab="run-history" />
                </WithPermissionAccess>
              }
            />
            {/* TODO: feature_flag_cleanup: hasContactLevelEventExportUi */}
            <Route
              path="data/jobs/export/:jobId"
              element={
                <WithFeatureAccess featureFlag="hasContactLevelEventExportUi">
                  <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                    <Navigate to="overview" />
                  </WithPermissionAccess>
                </WithFeatureAccess>
              }
            />
            <Route
              path="data/jobs/export/:jobId/overview"
              element={
                <WithFeatureAccess featureFlag="hasContactLevelEventExportUi">
                  <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                    <JobExportDetailPage currentTab="overview" />
                  </WithPermissionAccess>
                </WithFeatureAccess>
              }
            />
            <Route
              path="data/jobs/export/:jobId/run-history"
              element={
                <WithFeatureAccess featureFlag="hasContactLevelEventExportUi">
                  <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                    <JobExportDetailPage currentTab="run-history" />
                  </WithPermissionAccess>
                </WithFeatureAccess>
              }
            />
            {/* TODO: feature_flag_cleanup: hasDatasetsUi */}
            <Route
              path="data/datasets"
              element={
                <WithFeatureAccess featureFlag="hasDatasetsUi">
                  <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                    <DatasetsPage />
                  </WithPermissionAccess>
                </WithFeatureAccess>
              }
            />
            {/* TODO: feature_flag_cleanup: hasDatasetsUi */}
            <Route
              path="data/datasets/create"
              element={
                <WithFeatureAccess featureFlag="hasDatasetsUi">
                  <WithPermissionAccess permission={PERMISSIONS.DATA_CREATE}>
                    <DatasetCreatePage />
                  </WithPermissionAccess>
                </WithFeatureAccess>
              }
            />
            {/* TODO: feature_flag_cleanup: hasDatasetsUi */}
            <Route
              path="data/datasets/:datasetId"
              element={
                <WithFeatureAccess featureFlag="hasDatasetsUi">
                  <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                    <DatasetDetailPage />
                  </WithPermissionAccess>
                </WithFeatureAccess>
              }
            />
            {/* TODO: feature_flag_cleanup: hasDatasetsUi */}
            <Route
              path="data/datasets/:datasetId/source"
              element={
                <WithFeatureAccess featureFlag="hasDatasetsUi">
                  <WithPermissionAccess permission={PERMISSIONS.DATA_CREATE}>
                    <DatasetSourcePage />
                  </WithPermissionAccess>
                </WithFeatureAccess>
              }
            />
            {/* TODO: feature_flag_cleanup: hasDatasetsUi */}
            <Route
              path="data/datasets/:datasetId/jobs/:jobId"
              element={
                <WithFeatureAccess featureFlag="hasDatasetsUi">
                  <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                    <Navigate to="overview" replace />
                  </WithPermissionAccess>
                </WithFeatureAccess>
              }
            />
            {/* TODO: feature_flag_cleanup: hasDatasetsUi */}
            <Route
              path="data/datasets/:datasetId/jobs/:jobId/overview"
              element={
                <WithFeatureAccess featureFlag="hasDatasetsUi">
                  <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                    <DatasetJobDetailPage currentTab="overview" />
                  </WithPermissionAccess>
                </WithFeatureAccess>
              }
            />
            {/* TODO: feature_flag_cleanup: hasDatasetsUi */}
            <Route
              path="data/datasets/:datasetId/jobs/:jobId/run-history"
              element={
                <WithFeatureAccess featureFlag="hasDatasetsUi">
                  <WithPermissionAccess permission={PERMISSIONS.DATA_READ}>
                    <DatasetJobDetailPage currentTab="run-history" />
                  </WithPermissionAccess>
                </WithFeatureAccess>
              }
            />

            <Route
              path="brand-kit"
              element={<Navigate to="guidelines" replace />}
            />
            <Route
              path="brand-kit/:brandKitTab"
              element={
                <WithPermissionAccess permission={PERMISSIONS.BRANDKIT_READ}>
                  <BrandKitPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="brand-kit/:brandKitTab/create"
              element={
                <WithPermissionAccess permission={PERMISSIONS.BRANDKIT_CREATE}>
                  <BrandKitDetailsPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="brand-kit/blocks/:blockId/edit"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.BRANDKIT_UPDATE}
                  role={ROLE_NAME_MAP.editor}
                >
                  {/* TODO: feature_flag_cleanup: hasBrandKitBlocks */}
                  <WithFeatureAccess featureFlag="hasBrandKitBlocks">
                    <BlocksEditorPage />
                  </WithFeatureAccess>
                </WithPermissionAccess>
              }
            />
            <Route
              path="brand-kit/:brandKitTab/:brandKitId/edit"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.BRANDKIT_UPDATE}
                  role={ROLE_NAME_MAP.editor}
                >
                  <BrandKitDetailsPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="brand-kit/components/:componentId/edit"
              element={
                <WithPermissionAccess
                  permission={PERMISSIONS.BRANDKIT_UPDATE}
                  role={ROLE_NAME_MAP.editor}
                >
                  <ContentComponentsEditorPage />
                </WithPermissionAccess>
              }
            />
            <Route
              path="settings/:settingsTab"
              element={
                <WithPermissionAccess permission={PERMISSIONS.SETTINGS_READ}>
                  <SettingsPage />
                </WithPermissionAccess>
              }
            />
            <Route path="organizations" element={<Organizations />} />
            <Route
              path="organizations/:organizationId/:organizationTab?"
              element={<UsersSettings />}
            />
            <Route
              path="organizations/:organizationId/users/:userId"
              element={<UserEdit />}
            />
            <Route
              path="organizations/:organizationId/accounts/:accountId/:accountTab?"
              element={<AccountsEdit />}
            />
            <Route
              path="organizations/:organizationId/applications/:applicationId/:applicationEditTab"
              element={<ApplicationEditPage />}
            />
            {/* TODO: feature_flag_cleanup: hasAccountCustomRole */}
            <Route
              path="organizations/:organizationId/accounts/:accountId/roles/create"
              element={
                <WithFeatureAccess featureFlag="hasAccountCustomRole">
                  <WithStaffAccess>
                    <CreateRolePage />
                  </WithStaffAccess>
                </WithFeatureAccess>
              }
            />
            {/* TODO: feature_flag_cleanup: hasAccountCustomRole */}
            <Route
              path="organizations/:organizationId/accounts/:accountId/roles/:roleName/edit"
              element={
                <WithFeatureAccess featureFlag="hasAccountCustomRole">
                  <WithStaffAccess>
                    <CreateRolePage />
                  </WithStaffAccess>
                </WithFeatureAccess>
              }
            />
            <Route
              path="organizations/:organizationId/accounts/:accountId/roles/:roleName"
              element={<AccountsRolesEdit />}
            />

            <Route path="notifications" element={<NotificationSettings />} />
            <Route
              path="notifications/data"
              element={<NotificationSettings />}
            />
            <Route path="alerts" element={<NotificationSettings />} />
            <Route
              path="graphql"
              element={
                <WithDevelopersAccess>
                  <GraphiQLComponent />
                </WithDevelopersAccess>
              }
            />
            {/* /* wildcard is required — passes :journeyId/:journeyTab segments
                into the package's own <Routes> for self-contained subrouting */}
            <Route
              path="journey/*"
              element={
                <WithFeatureAccess featureFlag="hasJourneyBuilder">
                  <JourneyPage />
                </WithFeatureAccess>
              }
            />
          </Route>
        </Route>
        <Route path="/callback" element={<CallbackPage />} />
      </Route>
      <Route
        path="/studio/callback"
        element={<StudioAuthorizationCallback />}
      />
      <Route path="*" element={<NotFound />} />
    </SentryRoutes>
  );
};

export default Routes;
